import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FinanceService } from '@app/finance/finance.service';
import { UtilService } from '@app/shared/services/util.service';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { certiplate_logo } from '@app/shared/constants/application-constants';
import { ApiCallService } from '@app/services/api-call.service';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface LinkedAssessment {
  id: number;
  title: string;
  start_datetime: string;
  end_datetime: string;
  sector_name: string | null;
  project_name: string | null;
  custom_attributes: { label: string; type: string; value: any }[];
}

@Component({
    selector: 'app-invoice',
    templateUrl: './invoice.component.html',
    styleUrls: ['./invoice.component.css'],
    standalone: false
})
export class InvoiceComponent implements OnInit {
  private readonly pdfBrandColor = '#34495e';
  total: any;
  toWords: any;
  data: any;
  isLoading = true;
  organizationName: string;
  organizationAddress: string;
  orgEmail: string;
  organizationPan: string;
  linkedItems: { id: any; title: string; type: string }[] = [];
  linkedAssessments: LinkedAssessment[] = [];
  @ViewChild('pdfTable', { static: false }) pdfTable: ElementRef;
  constructor(
    public dialogRef: MatDialogRef<InvoiceComponent>,
    @Inject(MAT_DIALOG_DATA)
    public invoiceData: { id: string; customerId: string },
    private util: UtilService,
    private service: FinanceService,
    private apiService: ApiCallService
  ) {}

  ngOnInit(): void {
    this.toWords = this.util.getToWordsConfig();
    this.service
      .getInvoice(this.invoiceData.id, this.invoiceData.customerId)
      .subscribe((data: any) => {
        const respBody = data.body.responseBody;
        const response = Array.isArray(respBody) ? respBody[0] : respBody;
        this.data = response?.invoices?.[0];
        if (this.data) {
          this.data.customer_gstin = this.data.customer_gstin || response?.customer_gstin;
          this.data.customer_pan = this.data.customer_pan || response?.customer_pan;
          this.data.po_number = this.data.po_number || response?.po_number;
        }
        this.organizationName = response?.organization_name;
        this.organizationAddress = response?.organization_billing_address;
        this.orgEmail = response?.seller_email;
        this.organizationPan = response?.organization_pan;
        this.loadLinkedItems();
      });
  }


  formatAttributeValue(attr: { type: string; value: any }): string {
    const value = attr?.value;
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (String(attr?.type) === '2' && value && !isNaN(Date.parse(value))) {
      return formatDate(value, 'dd/MM/yyyy', 'en-US');
    }
    return value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
  }

  // Assessment details come enriched from the backend; question paper versions are resolved
  // here, mirroring the Link Items lookup in AddInvoice.
  private loadLinkedItems(): void {
    this.linkedAssessments = Array.isArray(this.data?.linked_assessments)
      ? this.data.linked_assessments
      : [];
    const assessmentIds = new Set(this.linkedAssessments.map((assessment) => Number(assessment.id)));
    const itemIds: any[] = (Array.isArray(this.data?.item_ids) ? this.data.item_ids : [])
      .map((id: any) => id?.id ?? id)
      .filter((id: any) => !assessmentIds.has(Number(id)));
    const itemTypes: string[] = Array.isArray(this.data?.item)
      ? this.data.item
      : this.data?.item ? [this.data.item] : [];
    if (itemIds.length === 0 || !itemTypes.includes('Question Paper Versions')) {
      this.isLoading = false;
      return;
    }

    this.apiService
      .getQuestionPaperVersion({})
      .pipe(
        map((data: any) => {
          const response = data?.body?.responseBody || [];
          return (Array.isArray(response) ? response : []).map((item: any) => ({
            id: item.question_paper_version_id,
            title: item.question_paper_version_name,
            type: 'Question Paper Versions',
          }));
        }),
        catchError(() => of([]))
      )
      .subscribe((versions: any[]) => {
        this.linkedItems = itemIds
          .map((id) => versions.find((item) => item.id == id))
          .filter((item) => !!item);
        this.isLoading = false;
      });
  }


  private addPdfBranding(
    pdf: jsPDF,
    downloadedAt: string,
    pageNo: number,
    totalPages: number
  ): void {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const logoMaxWidth = 56;
    const logoMaxHeight = 18;
    const logoProps = pdf.getImageProperties(certiplate_logo);
    const logoRatio = logoProps.width / logoProps.height;
    let logoWidth = logoMaxWidth;
    let logoHeight = logoWidth / logoRatio;
    if (logoHeight > logoMaxHeight) {
      logoHeight = logoMaxHeight;
      logoWidth = logoHeight * logoRatio;
    }
    pdf.addImage(certiplate_logo, 'PNG', 10, 5, logoWidth, logoHeight);
    pdf.setDrawColor(this.pdfBrandColor);
    pdf.setLineWidth(0.2);
    pdf.line(10, 25, pageWidth - 10, 25);
    pdf.line(10, pageHeight - 11, pageWidth - 10, pageHeight - 11);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(this.pdfBrandColor);
    pdf.text(`Downloaded on: ${downloadedAt}`, 10, pageHeight - 6);
    pdf.text('Powered by Certiplate', pageWidth / 2, pageHeight - 6, {
      align: 'center',
    });
    pdf.text(`Page ${pageNo} of ${totalPages}`, pageWidth - 10, pageHeight - 6, {
      align: 'right',
    });
  }

  download() {
    const div = this.pdfTable.nativeElement;


    html2canvas(div, {
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true,
      logging: false, // Disable logging for better performance
      backgroundColor: '#ffffff',
      width: div.scrollWidth,
      height: div.scrollHeight,
      onclone: (clonedDoc: Document) => {
        const printable = clonedDoc.getElementById('printable');
        if (printable) {
          printable.style.maxHeight = 'none';
          printable.style.overflow = 'visible';
        }
      },
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const sideMargin = 10;
      const headerHeight = 30;
      const footerHeight = 14;
      const contentWidth = pageWidth - sideMargin * 2;
      const contentHeight = pageHeight - headerHeight - footerHeight;
      const scaledImageHeight = (canvas.height * contentWidth) / canvas.width;
      const totalPages = Math.ceil(scaledImageHeight / contentHeight);
      const downloadedAt = new Date().toLocaleString();

      let yOffset = 0;
      let pageNo = 1;

      while (yOffset < scaledImageHeight) {
        if (pageNo > 1) {
          pdf.addPage();
        }

        pdf.addImage(
          imgData,
          'PNG',
          sideMargin,
          headerHeight - yOffset,
          contentWidth,
          scaledImageHeight
        );


        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight - 1, 'F');
        pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

        this.addPdfBranding(pdf, downloadedAt, pageNo, totalPages);
        yOffset += contentHeight;
        pageNo += 1;
      }
  
      pdf.save(`Invoice_${new Date().toISOString()}.pdf`);
    }).catch((error) => {
      console.error('Error generating PDF:', error);
    });
  }
  




  
  
  
  
  
  
  
  
  
  
  
  
  
  
}
